import React, { useState, useEffect } from 'react';
import LeadDetail from './LeadDetail';
import { tokens as tk } from './theme';

const API = 'https://tcio-api.onrender.com';

const DOT = '\u00B7';
const CHECK = '\u2713';
const DASH = '\u2014';
const ELLIP = '\u2026';
const TIMES = '\u2715';

const STAGES = [
  { key: 'new',            label: 'New' },
  { key: 'contacted',      label: 'Contacted' },
  { key: 'vob_pending',    label: 'VOB Pending' },
  { key: 'vob_completed',  label: 'VOB Complete' },
  { key: 'prescreen_done', label: 'Prescreen Done' },
  { key: 'pending_admit',  label: 'Pending Admit' },
  { key: 'admitted',       label: 'Admitted' },
];
const STAGE_LABEL = {};
STAGES.forEach(x => { STAGE_LABEL[x.key] = x.label; });
STAGE_LABEL.lost = 'Lost'; STAGE_LABEL.not_viable = 'Not Viable';

const stageDot = (st) => st === 'admitted' ? tk.success
  : st === 'vob_completed' ? tk.success
  : st === 'pending_admit' ? tk.warning
  : st === 'vob_pending' ? tk.warning
  : st === 'lost' || st === 'not_viable' ? tk.textMuted
  : tk.blue500;

const grade = (rate) => {
  const r = parseFloat(rate || 0);
  if (r >= 20) return { t: 'A', c: tk.success };
  if (r >= 10) return { t: 'B', c: tk.blue600 };
  if (r >= 5)  return { t: 'C', c: tk.warning };
  return { t: 'D', c: tk.danger };
};

const money = (n) => n ? '$' + Math.round(parseFloat(n)).toLocaleString() : DASH;
const initials = (f, l) => ((f || ' ')[0] + (l || ' ')[0]).toUpperCase();
const AV_COLORS = [tk.blue600, tk.blue700, tk.ai, tk.success, tk.warning, tk.blue400];
const avColor = (name) => AV_COLORS[((name || '').charCodeAt(0) || 0) % AV_COLORS.length];

const ago = (t) => {
  if (!t) return DASH;
  const d = Math.floor((new Date() - new Date(t)) / 86400000);
  if (d === 0) return 'today';
  if (d === 1) return 'yesterday';
  return d + 'd ago';
};
const ageDays = (t) => t ? Math.floor((new Date() - new Date(t)) / 86400000) + 'd' : DASH;
const timeAt = (t) => new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const nextActionOf = (l, isCold, coldDays) => {
  if (['lost', 'not_viable'].includes(l.pipeline_stage)) return { label: 'Closed', c: tk.textMuted };
  if (l.pipeline_stage === 'admitted') return { label: CHECK + ' Complete', c: tk.success };
  if (l.callback_due) return { label: 'Callback overdue', c: tk.danger, strong: true };
  if (l.callback_at && new Date(l.callback_at) > new Date()) return { label: 'Callback ' + timeAt(l.callback_at), c: tk.ink };
  if (l.pipeline_stage === 'pending_admit') return { label: 'Confirm arrival', c: tk.ink };
  if (l.pipeline_stage === 'prescreen_done') return { label: 'Move to admit', c: tk.ink };
  if (l.pipeline_stage === 'vob_completed') return { label: 'Call ' + DASH + ' VOB back', c: tk.success, strong: true };
  if (l.pipeline_stage === 'vob_pending') return { label: 'Chase VOB', c: tk.textSecondary };
  if (l.pipeline_stage === 'new') return { label: 'First call', c: tk.ink };
  if (isCold) return { label: l.days_since_activity + 'd cold ' + DASH + ' re-engage', c: tk.warning, strong: true };
  return { label: 'Follow up', c: tk.textSecondary };
};

const SPINE = ['Inquiry', 'Contacted', 'VOB', 'Prescreen', 'Pending Admit', 'Admitted'];
const SPINE_DONE = { new: 1, contacted: 2, vob_pending: 2, vob_completed: 3, prescreen_done: 4, pending_admit: 5, admitted: 6 };

export default function Leads({ user }) {
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [coldDays, setColdDays] = useState(5);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [repFilter, setRepFilter] = useState('all');
  const [facFilter, setFacFilter] = useState('all');
  const [reps, setReps] = useState([]);
  const [scorecard, setScorecard] = useState(null);
  const [showScorecard, setShowScorecard] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [facList, setFacList] = useState([]);

  const headers = { Authorization: 'Bearer ' + user.token };
  const isAdmin = ['ceo', 'admin'].includes(user.role);

  useEffect(() => {
    const onR = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filter === 'cold') q.set('cold', 'true');
      else if (filter !== 'all' && filter !== 'vob') q.set('stage', filter);
      if (repFilter === 'me') q.set('assignedTo', 'me');
      else if (repFilter !== 'all') q.set('assignedTo', repFilter);
      if (facFilter !== 'all') q.set('facilityId', facFilter);

      const r = await fetch(API + '/api/leads/v2?' + q, { headers });
      const d = await r.json();
      if (d.ok) { setLeads(d.data); setCounts(d.counts); setColdDays(d.cold_days); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const restoreLead = async (id) => {
    try {
      const r = await fetch(API + '/api/leads/v2/' + id + '/restore', { method: 'POST', headers });
      const d = await r.json();
      if (d.ok) load();
      else alert(d.error || 'Restore failed');
    } catch (e) { alert('Restore failed'); }
  };

  useEffect(() => {
    const ALLOW = ['andrew marquez', 'aurora carlsen', 'tiffini westfall', 'andre marquez', 'allison smith', 'jaclyn hicks'];
    fetch(API + '/api/reps', { headers }).then(r => r.json())
      .then(d => d.ok && setReps((d.data || []).filter(u => ALLOW.includes(((u.first_name || '') + ' ' + (u.last_name || '')).toLowerCase().trim())))).catch(() => {});
    fetch(API + '/api/facilities', { headers }).then(r => r.json()).then(j => {
      const arr = Array.isArray(j) ? j : (j.data || j.facilities || []);
      setFacList(arr.map(f => ({ id: f.id, name: f.org_name || f.name })).filter(f => f.id && f.name));
    }).catch(() => {});
    if (isAdmin) {
      fetch(API + '/api/leads/v2/rep-scorecard', { headers }).then(r => r.json())
        .then(d => d.ok && setScorecard(d)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter, repFilter, facFilter]);

  useEffect(() => {
    const openFromHash = () => {
      const m = window.location.hash.match(/^#\/lead\/([0-9a-f-]{36})/i);
      if (m) setSelected(m[1]);
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
  }, []);

  if (selected) {
    return <LeadDetail
      leadId={selected}
      user={user}
      onBack={() => { setSelected(null); if (window.location.hash.startsWith('#/lead/')) window.history.replaceState(null, '', window.location.pathname + window.location.search); load(); }}
    />;
  }

  const NOT_COLD_STAGES = ['admitted', 'lost', 'not_viable'];
  const isColdFn = (l) => l.days_since_activity >= coldDays && !NOT_COLD_STAGES.includes(l.pipeline_stage);
  const coldCount = leads.filter(isColdFn).length;

  const facilities = facList;

  const matchSearch = (l) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const ph = (l.phone || '').replace(/\D/g, '');
    return ((l.first_name || '') + ' ' + (l.last_name || '')).toLowerCase().includes(q)
      || ph.includes(q.replace(/\D/g, '') || '~')
      || (l.payer_name || '').toLowerCase().includes(q)
      || (l.pipeline_stage || '').toLowerCase().includes(q);
  };
  const matchVob = (l) => filter !== 'vob' || ['vob_pending', 'vob_completed'].includes(l.pipeline_stage);
  const visible = leads.filter(l => matchSearch(l) && matchVob(l));

  const activeTotal = STAGES.filter(x => x.key !== 'admitted').reduce((n, x) => n + (counts[x.key] || 0), 0);
  const vobCount = (counts.vob_pending || 0) + (counts.vob_completed || 0);

  const TABS = [
    ['all', 'All', activeTotal + (counts.admitted || 0)],
    ['new', 'New', counts.new || 0],
    ['contacted', 'Contacted', counts.contacted || 0],
    ['vob', 'VOB', vobCount],
    ['prescreen_done', 'Prescreen', counts.prescreen_done || 0],
    ['pending_admit', 'Pending Admit', counts.pending_admit || 0],
    ['admitted', 'Admitted', counts.admitted || 0],
    ['cold', 'Cold', null],
    ['lost', 'Lost', null],
    ['not_viable', 'Not Viable', null],
  ];

  const summary = [
    ['all', activeTotal + ' Active'],
    ['new', (counts.new || 0) + ' New'],
    ['contacted', (counts.contacted || 0) + ' Contacted'],
    ['vob', vobCount + ' VOB'],
    ['prescreen_done', (counts.prescreen_done || 0) + ' Prescreen'],
    ['pending_admit', (counts.pending_admit || 0) + ' Pending Admit'],
  ];

  const Expanded = (l) => {
    const g = l.actual_collected_rate ? grade(l.actual_collected_rate) : null;
    const done = SPINE_DONE[l.pipeline_stage] != null ? SPINE_DONE[l.pipeline_stage] : 1;
    const dead = ['lost', 'not_viable'].includes(l.pipeline_stage);
    return (
      <div style={{ ...s.expand, gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1.6fr 1fr' }} onClick={e => e.stopPropagation()}>
        <div>
          <div style={s.secTitle}>LEAD SNAPSHOT</div>
          <div style={s.kv}><span style={s.k}>Phone</span><span style={s.v}>{l.phone || DASH}</span></div>
          {l.email && <div style={s.kv}><span style={s.k}>Email</span><span style={s.v}>{l.email}</span></div>}
          {l.facility_name && <div style={s.kv}><span style={s.k}>Facility</span><span style={s.v}>{l.facility_name}</span></div>}
          {l.loc_requested && <div style={s.kv}><span style={s.k}>LOC</span><span style={s.v}>{l.loc_requested}</span></div>}
          {l.payer_name && <div style={s.kv}><span style={s.k}>Insurance</span><span style={s.v}>{l.payer_name}{g && <span style={{ ...s.gradeChip, background: g.c }}>{g.t}</span>}</span></div>}
          {l.avg_day_rate_collected && <div style={s.kv}><span style={s.k}>Value</span><span style={s.v}>{money(l.avg_day_rate_collected)}/day{l.avg_episode_value ? <> {DOT} {money(l.avg_episode_value)} est.</> : null}{l.rate_source === 'company' ? ' (company avg)' : ''}</span></div>}
        </div>
        <div>
          <div style={s.secTitle}>ADMISSION PROGRESS</div>
          {dead ? (
            <div style={{ fontSize: 13, color: tk.textMuted }}>Lead is {STAGE_LABEL[l.pipeline_stage]}. Restore to put it back in the pipeline.</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: 6, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              {SPINE.map((st, i) => (
                <div key={st} style={{ display: 'flex', alignItems: 'center', flex: i < SPINE.length - 1 ? 1 : 'none' }}>
                  <div style={{ textAlign: 'center', minWidth: 50 }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: 10, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700,
                      background: i < done ? tk.success : i === done ? tk.ai : tk.surfaceSoft,
                      color: i <= done ? '#FFFFFF' : tk.textMuted,
                      border: '1px solid ' + (i < done ? tk.success : i === done ? tk.ai : tk.border)
                    }}>{i < done ? CHECK : i + 1}</div>
                    <div style={{ fontSize: 9.5, fontWeight: 600, marginTop: 3, color: i === done ? tk.ai : i < done ? tk.ink : tk.textMuted }}>{st}</div>
                  </div>
                  {i < SPINE.length - 1 && <div style={{ flex: 1, height: 2, background: i < done ? tk.success : tk.borderSoft, margin: '0 2px 12px' }} />}
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 12, color: tk.textSecondary, marginTop: 10 }}>Last activity: <span style={{ fontWeight: 600, color: tk.ink }}>{ago(l.last_activity_at)}</span> {DOT} Created {ageDays(l.created_at)} ago</div>
        </div>
        <div>
          <div style={s.secTitle}>ACTIONS</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 180 }}>
            <a href={'#/lead/' + l.id} onClick={(e) => { e.preventDefault(); setSelected(l.id); }} style={s.btnPrimary}>Open Lead</a>
            {dead && <button onClick={() => restoreLead(l.id)} style={s.btnGhost}>Restore</button>}
          </div>
        </div>
      </div>
    );
  };

  const RowD = (l) => {
    const isCold = isColdFn(l);
    const g = l.actual_collected_rate ? grade(l.actual_collected_rate) : null;
    const na = nextActionOf(l, isCold, coldDays);
    const open = expandedId === l.id;
    const urgent = !!l.callback_due;
    return (
      <div key={l.id}>
        <div onClick={() => setExpandedId(open ? null : l.id)}
          style={{ ...s.row, gridTemplateColumns: s.gridCols, background: open ? tk.aiSoft : tk.surface, borderLeft: urgent ? '3px solid ' + tk.danger : isCold ? '3px solid ' + tk.warning : '3px solid transparent' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
            <span style={{ ...s.avatar, background: avColor(l.first_name) }}>{initials(l.first_name, l.last_name)}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: tk.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.first_name} {l.last_name}{l.returning_client ? <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: '#E6F1FD', color: '#0B5FBF', whiteSpace: 'nowrap' }}>Returning</span> : null}</div>
              <div style={{ fontSize: 11.5, color: tk.textMuted }}>{l.phone || ''}</div>
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            {l.payer_name ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {g && <span style={{ ...s.gradeChip, background: g.c, marginLeft: 0 }}>{g.t}</span>}
                  <span style={{ fontSize: 12.5, fontWeight: 500, color: tk.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.payer_name}</span>
                </div>
                {l.avg_day_rate_collected && <div style={{ fontSize: 11, color: tk.textMuted }}>{money(l.avg_day_rate_collected)}/day{l.avg_episode_value ? <> {DOT} {money(l.avg_episode_value)} est.</> : null}{l.rate_source === 'company' ? ' (company avg)' : ''}</div>}
              </div>
            ) : <span style={{ fontSize: 12, color: tk.textMuted }}>No payer yet</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: 4, background: stageDot(l.pipeline_stage), flex: 'none' }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: tk.textSecondary, whiteSpace: 'nowrap' }}>{STAGE_LABEL[l.pipeline_stage] || l.pipeline_stage}</span>
          </div>
          <div style={{ fontSize: 12.5, fontWeight: na.strong ? 700 : 500, color: na.c, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{na.label}</div>
          <div>
            {l.rep_first ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ ...s.avatar, width: 20, height: 20, fontSize: 8.5, background: avColor(l.rep_first) }}>{initials(l.rep_first, l.rep_last)}</span>
                <span style={{ fontSize: 12, color: tk.textSecondary, whiteSpace: 'nowrap' }}>{l.rep_first} {(l.rep_last || ' ')[0]}.</span>
              </div>
            ) : <span style={{ fontSize: 11, fontWeight: 700, color: tk.warning }}>Unassigned</span>}
          </div>
          <div style={{ fontSize: 12, color: isCold ? tk.warning : tk.textSecondary, fontWeight: isCold ? 600 : 400, whiteSpace: 'nowrap' }}>{isCold ? l.days_since_activity + 'd cold' : ago(l.last_activity_at)}</div>
          <div style={{ fontSize: 12, color: tk.textMuted }}>{ageDays(l.created_at)}</div>
          <div style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
            {['lost', 'not_viable'].includes(l.pipeline_stage) ? (
              <button onClick={() => restoreLead(l.id)} style={s.btnSmall}>Restore</button>
            ) : (
              <button onClick={() => setExpandedId(open ? null : l.id)} style={s.btnSmall}>{open ? TIMES : ELLIP}</button>
            )}
          </div>
        </div>
        {open && Expanded(l)}
      </div>
    );
  };

  const RowM = (l) => {
    const isCold = isColdFn(l);
    const na = nextActionOf(l, isCold, coldDays);
    const open = expandedId === l.id;
    const urgent = !!l.callback_due;
    return (
      <div key={l.id} style={{ ...s.mcard, borderLeft: urgent ? '3px solid ' + tk.danger : isCold ? '3px solid ' + tk.warning : '3px solid ' + tk.border }}>
        <div onClick={() => setExpandedId(open ? null : l.id)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ ...s.avatar, background: avColor(l.first_name) }}>{initials(l.first_name, l.last_name)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: tk.ink }}>{l.first_name} {l.last_name}{l.returning_client ? <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: '#E6F1FD', color: '#0B5FBF', whiteSpace: 'nowrap' }}>Returning</span> : null}</div>
              <div style={{ fontSize: 11.5, color: tk.textMuted }}>{l.payer_name || l.phone || ''}</div>
            </div>
            <span style={{ fontSize: 11, fontWeight: 600, color: tk.textSecondary, whiteSpace: 'nowrap' }}>{STAGE_LABEL[l.pipeline_stage] || l.pipeline_stage}</span>
          </div>
          <div style={{ fontSize: 12.5, fontWeight: na.strong ? 700 : 500, color: na.c, marginTop: 6 }}>{na.label} <span style={{ fontWeight: 400, color: tk.textMuted }}>{DOT} {l.rep_first ? l.rep_first : 'Unassigned'} {DOT} {ago(l.last_activity_at)}</span></div>
        </div>
        {open && Expanded(l)}
      </div>
    );
  };

  return (
    <div style={s.wrap}>
      <div style={{ ...s.head, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={s.h1}>Leads</h1>
          <p style={s.sub}>{activeTotal} active</p>
        </div>
        {isAdmin && scorecard && (
          <button onClick={() => setShowScorecard(!showScorecard)} style={s.btnHeader}>
            Rep Accountability
            {scorecard.data.some(r => r.cold_leads > 0) && (
              <span style={s.alertDot}>{(() => { const n = scorecard.data.reduce((a, r) => a + r.cold_leads, 0); return (n > 99 ? '99+' : n) + ' cold'; })()}</span>
            )}
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12, fontSize: 12.5 }}>
        {summary.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: tk.font, fontSize: 12.5, fontWeight: filter === k ? 700 : 500, color: filter === k ? tk.ai : tk.textSecondary }}>
            {label}
          </button>
        ))}
      </div>

      {showScorecard && scorecard && <Accountability data={scorecard} coldDays={coldDays} />}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={'Search leads ' + DASH + ' name, phone, payer, or stage' + ELLIP}
            style={s.search} />
          {search && <button onClick={() => setSearch('')} style={s.searchX}>{TIMES}</button>}
        </div>
        <select value={repFilter} onChange={e => setRepFilter(e.target.value)} style={s.select}>
          <option value="all">All reps</option>
          <option value="me">My leads</option>
          {reps.map(r => <option key={r.id} value={r.id}>{r.first_name} {r.last_name}</option>)}
        </select>
        {facilities.length > 1 && (
          <select value={facFilter} onChange={e => setFacFilter(e.target.value)} style={s.select}>
            <option value="all">All facilities</option>
            {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        )}
      </div>

      <div style={{ display: 'flex', gap: 2, marginBottom: 14, flexWrap: 'wrap', borderBottom: '1px solid ' + tk.borderSoft }}>
        {TABS.map(([k, label, n]) => (
          <button key={k} onClick={() => setFilter(k)}
            style={{ ...s.tab, color: filter === k ? tk.ai : k === 'cold' && coldCount > 0 && filter !== k ? tk.warning : tk.textSecondary, borderBottom: filter === k ? '2px solid ' + tk.ai : '2px solid transparent', fontWeight: filter === k ? 700 : 500 }}>
            {label}{n != null && n > 0 ? ' ' + n : k === 'cold' && coldCount > 0 ? ' ' + coldCount : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={s.empty}>Loading{ELLIP}</div>
      ) : visible.length === 0 ? (
        <div style={s.empty}>No leads here.</div>
      ) : !isMobile ? (
        <div style={s.table}>
          <div style={{ ...s.thead, gridTemplateColumns: s.gridCols }}>
            <div>Lead</div><div>Payer</div><div>Stage</div><div>Next Action</div><div>Owner</div><div>Last Activity</div><div>Age</div><div></div>
          </div>
          {visible.map(RowD)}
        </div>
      ) : (
        <div>{visible.map(RowM)}</div>
      )}
    </div>
  );
}

function Accountability({ data, coldDays }) {
  return (
    <div style={s.card}>
      <div style={s.cardTitle}>Rep Accountability {DASH} cold = {coldDays}+ days no activity</div>
      {data.unassigned > 0 && (
        <div style={s.unassigned}>{data.unassigned} unassigned lead{data.unassigned > 1 ? 's' : ''}</div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {['Rep', 'Leads', 'Cold', 'Never contacted', 'Callbacks overdue', 'Converted', 'Avg days stale']
                .map(h => <th key={h} style={s.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.data.filter(r => r.total_leads > 0).map(r => (
              <tr key={r.id} style={{ borderTop: '1px solid ' + tk.borderSoft }}>
                <td style={{ ...s.td, fontWeight: 600 }}>{r.first_name} {r.last_name}</td>
                <td style={s.td}>{r.total_leads}</td>
                <td style={{ ...s.td, color: r.cold_leads > 0 ? tk.danger : tk.textMuted, fontWeight: r.cold_leads > 0 ? 700 : 400 }}>{r.cold_leads}</td>
                <td style={{ ...s.td, color: r.never_contacted > 0 ? tk.danger : tk.textMuted, fontWeight: r.never_contacted > 0 ? 700 : 400 }}>{r.never_contacted}</td>
                <td style={{ ...s.td, color: r.callbacks_overdue > 0 ? tk.danger : tk.textMuted, fontWeight: r.callbacks_overdue > 0 ? 700 : 400 }}>{r.callbacks_overdue}</td>
                <td style={{ ...s.td, color: tk.success, fontWeight: 600 }}>{r.converted}</td>
                <td style={s.td}>{r.avg_days_stale || '\u2014'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const s = {
  gridCols: '1.5fr 1.3fr 1fr 1.2fr 1fr 0.9fr 0.5fr 60px',
  wrap: { padding: 24, maxWidth: 1280, margin: '0 auto', fontFamily: tk.font, background: tk.canvas, minHeight: '100vh', boxSizing: 'border-box' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  h1: { fontSize: 24, fontWeight: 700, color: tk.ink, margin: 0 },
  sub: { color: tk.textSecondary, margin: '4px 0 0', fontSize: 14 },
  btnHeader: { background: tk.surface, color: tk.blue700, border: '1px solid ' + tk.blue600, borderRadius: tk.radiusControl, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontFamily: tk.font },
  alertDot: { background: tk.warningBg, color: tk.warning, borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 700 },
  search: { width: '100%', padding: '10px 12px', border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, fontSize: 14, boxSizing: 'border-box', background: tk.surface, color: tk.ink, fontFamily: tk.font },
  searchX: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: tk.textMuted, fontSize: 14 },
  select: { fontSize: 13, fontWeight: 600, padding: '9px 10px', borderRadius: tk.radiusControl, border: '1px solid ' + tk.border, background: tk.surface, color: tk.ink, cursor: 'pointer', fontFamily: tk.font },
  tab: { background: 'none', border: 'none', padding: '8px 11px', fontSize: 13, cursor: 'pointer', fontFamily: tk.font, whiteSpace: 'nowrap' },
  table: { background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusCard, overflow: 'hidden', boxShadow: tk.shadow },
  thead: { display: 'grid', gap: 10, padding: '9px 14px', background: tk.surfaceSoft, borderBottom: '1px solid ' + tk.border, fontSize: 10, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' },
  row: { display: 'grid', gap: 10, padding: '10px 14px', borderBottom: '1px solid ' + tk.borderSoft, cursor: 'pointer', alignItems: 'center' },
  avatar: { width: 28, height: 28, borderRadius: 14, color: '#FFFFFF', fontSize: 10.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' },
  gradeChip: { color: '#FFFFFF', fontSize: 9, fontWeight: 700, borderRadius: 4, padding: '1px 5px', marginLeft: 6, flex: 'none' },
  expand: { display: 'grid', gap: 20, padding: '16px 18px', background: tk.surfaceSoft, borderBottom: '1px solid ' + tk.border, borderLeft: '3px solid ' + tk.ai },
  secTitle: { fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 },
  kv: { display: 'flex', gap: 8, fontSize: 12, marginBottom: 4 },
  k: { color: tk.textMuted, minWidth: 62, flex: 'none' },
  v: { color: tk.ink, fontWeight: 500 },
  btnPrimary: { background: tk.ai, color: '#FFFFFF', border: 'none', borderRadius: tk.radiusControl, padding: '9px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', textAlign: 'center', textDecoration: 'none', fontFamily: tk.font },
  btnGhost: { background: 'none', color: tk.textSecondary, border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, padding: '8px 12px', fontSize: 13, cursor: 'pointer', fontFamily: tk.font },
  btnSmall: { background: tk.surfaceSoft, border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, fontSize: 11.5, fontWeight: 700, padding: '4px 10px', cursor: 'pointer', color: tk.textSecondary, fontFamily: tk.font },
  mcard: { background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusCard, padding: 12, marginBottom: 10, boxShadow: tk.shadow, cursor: 'pointer' },
  card: { background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusCard, padding: 16, marginBottom: 14, boxShadow: tk.shadow },
  cardTitle: { fontSize: 12, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 },
  unassigned: { background: tk.warningBg, color: tk.warning, padding: '8px 12px', borderRadius: tk.radiusControl, fontSize: 13, fontWeight: 600, marginBottom: 12 },
  th: { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '10px', color: tk.ink },
  empty: { padding: 60, textAlign: 'center', color: tk.textMuted },
};
